import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setIsDropdownOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/98 backdrop-blur-md shadow-sm border-b border-background-200/70"
          : "bg-background-50/85 backdrop-blur-sm border-b border-background-200/40"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group shrink-0"
          >
            <span className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              ACBuy
            </span>
            <span className="text-sm md:text-base text-foreground-500 font-light tracking-wider mt-1">
              Wiki
            </span>
          </Link>

          {/* Desktop nav items */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Home
            </Link>

            <Link
              to="/acbuyspreadsheet-guide"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-book-open-line mr-1.5"></i>
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
            >
              Main Platform
            </a>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-3 right-0 w-64 bg-background-50 border border-background-200/70 rounded-lg py-2 shadow-lg z-50 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.url)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 flex items-center justify-between whitespace-nowrap cursor-pointer"
                    >
                      <span>{cat.name}</span>
                      <i className="ri-external-link-line text-xs text-foreground-400"></i>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex items-center justify-center w-10 h-10 text-foreground-800 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {/* Primary nav */}
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors flex items-center gap-2"
            >
              <i className="ri-home-4-line"></i>
              Home
            </Link>

            <Link
              to="/acbuyspreadsheet-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors flex items-center gap-2"
            >
              <i className="ri-book-open-line"></i>
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg transition-colors flex items-center gap-2"
            >
              <i className="ri-global-line"></i>
              Main Platform
            </a>

            {/* Divider + Categories header */}
            <div className="border-t border-background-200/60 my-2"></div>

            <button
              onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
              className="px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors flex items-center justify-between cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-apps-2-line"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>

            {isMobileCatOpen && (
              <div className="pl-4 border-l-2 border-primary-200 ml-4 mt-1 space-y-0.5">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      handleCategorySelect(cat.url);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2.5 text-sm text-foreground-600 hover:bg-background-100 rounded-lg transition-colors flex items-center justify-between cursor-pointer whitespace-nowrap"
                  >
                    <span>{cat.name}</span>
                    <span className="text-xs text-foreground-400">{cat.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Divider + CTA */}
            <div className="border-t border-background-200/60 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold text-center hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-shopping-cart-2-line"></i>
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}